if (!customElements.get('inline-variant-picker')) {
  customElements.define(
    'inline-variant-picker',
    class InlineVariantPicker extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.selects = this.querySelectorAll('.inline-variant-picker__select');
        this.swatchContainers = this.querySelectorAll('.inline-variant-picker__swatches');
        this.buttonContainers = this.querySelectorAll('.inline-variant-picker__buttons');

        // Ohne Auswahlmöglichkeit gibt es nichts zu tun — und nichts nachzuladen.
        if (!this.selects.length && !this.swatchContainers.length && !this.buttonContainers.length) {
          return;
        }

        this.variantData = null;
        this.productGallery = [];
        this._dataPromise = null;
        this.cardWrapper = this.closest('.card-wrapper');
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('.quick-add__submit');
        this.submitButtonText = this.submitButton?.querySelector('span:first-child');
        this.soldOutMessage = this.submitButton?.querySelector('.sold-out-message');
        this.variantIdInput = this.form?.querySelector('input[name="id"]');

        this.selects.forEach((select) => {
          select.addEventListener('change', this.onVariantChange.bind(this));
        });

        this.swatchContainers.forEach((container) => {
          container.querySelectorAll('.inline-variant-picker__swatch').forEach((swatch) => {
            swatch.addEventListener('click', (e) => {
              const btn = e.currentTarget;
              container.querySelectorAll('.inline-variant-picker__swatch').forEach((s) =>
                s.classList.remove('inline-variant-picker__swatch--active')
              );
              btn.classList.add('inline-variant-picker__swatch--active');
              this.onVariantChange();
            });
          });
        });

        this.buttonContainers.forEach((container) => {
          container.querySelectorAll('.inline-variant-picker__btn').forEach((btn) => {
            btn.addEventListener('click', (e) => {
              const clicked = e.currentTarget;
              container.querySelectorAll('.inline-variant-picker__btn').forEach((b) =>
                b.classList.remove('inline-variant-picker__btn--active')
              );
              clicked.classList.add('inline-variant-picker__btn--active');
              this.onVariantChange();
            });
          });
        });

        // Stehen die Daten ausnahmsweise doch im HTML, direkt übernehmen.
        const dataEl = this.querySelector('[data-inline-variant-data]');
        if (dataEl) this.setVariantData(dataEl.textContent);

        // Sonst vorab holen, sobald Interesse erkennbar wird — dann liegen die
        // Daten beim ersten Klick meist schon bereit.
        if (!this.variantData) {
          const prefetch = () => this.loadVariantData();
          ['pointerenter', 'focusin', 'touchstart'].forEach((evt) => {
            this.addEventListener(evt, prefetch, { once: true, passive: true });
          });
        }
      }

      setVariantData(text) {
        try {
          const parsed = JSON.parse(text);
          if (!parsed || !Array.isArray(parsed.variants)) return false;
          this.variantData = parsed;
          // Galerie, die sich alle Varianten ohne eigenes variant_gallery-Metafeld teilen
          this.productGallery = parsed.product_gallery || [];
          return true;
        } catch (e) {
          console.warn('inline-variant-picker: JSON nicht lesbar', e);
          return false;
        }
      }

      // Holt die Variantendaten per Section Rendering API nach.
      // Mehrfachaufrufe teilen sich denselben Request.
      loadVariantData() {
        if (this.variantData) return Promise.resolve(this.variantData);
        if (this._dataPromise) return this._dataPromise;

        const url = this.dataset.variantDataUrl;
        if (!url) return Promise.resolve(null);

        this._dataPromise = fetch(url)
          .then((res) => {
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return res.text();
          })
          .then((html) => {
            const el = new DOMParser()
              .parseFromString(html, 'text/html')
              .querySelector('[data-inline-variant-data]');
            return el && this.setVariantData(el.textContent) ? this.variantData : null;
          })
          .catch((e) => {
            console.warn('inline-variant-picker: Variantendaten nicht ladbar', e);
            this._dataPromise = null; // beim nächsten Versuch erneut laden
            return null;
          });

        return this._dataPromise;
      }

      getSelectedOptions() {
        const options = [];
        const optionElements = this.querySelectorAll('.inline-variant-picker__option');

        optionElements.forEach((optionEl) => {
          const select = optionEl.querySelector('.inline-variant-picker__select');
          const activeSwatch = optionEl.querySelector('.inline-variant-picker__swatch--active');
          const activeBtn = optionEl.querySelector('.inline-variant-picker__btn--active');

          if (select) {
            options.push(select.value);
          } else if (activeSwatch) {
            options.push(activeSwatch.dataset.value);
          } else if (activeBtn) {
            options.push(activeBtn.dataset.value);
          }
        });

        return options;
      }

      onVariantChange() {
        if (this.variantData) {
          this.applyVariant();
          return;
        }

        // Solange die Daten fehlen, zeigt das versteckte id-Feld noch auf die
        // Standardvariante — bis dahin darf das Formular nicht abgeschickt werden.
        this.submitButton?.setAttribute('disabled', 'disabled');

        this.loadVariantData().then((data) => {
          // Ohne Daten laesst sich die Auswahl keiner Variante zuordnen. Der Button
          // bleibt gesperrt, sonst landet die Standardvariante im Warenkorb statt der
          // gewaehlten. Die naechste Interaktion versucht es erneut (_dataPromise ist
          // im Fehlerfall zurueckgesetzt).
          if (!data) return;
          // applyVariant setzt den Button-Zustand selbst.
          if (!this.applyVariant()) this.submitButton?.removeAttribute('disabled');
        });
      }

      applyVariant() {
        const variant = this.findVariant(this.getSelectedOptions());
        if (!variant) return false;

        this.updateVariantInput(variant);
        this.updateButton(variant);
        this.updatePrice(variant);
        this.updateImages(variant);
        return true;
      }

      findVariant(selectedOptions) {
        if (!this.variantData) return null;
        return this.variantData.variants.find((v) =>
          v.options.every((opt, i) => opt === selectedOptions[i])
        );
      }

      updateVariantInput(variant) {
        if (this.variantIdInput) {
          this.variantIdInput.value = variant.id;
          if (variant.available) {
            this.variantIdInput.removeAttribute('disabled');
          } else {
            this.variantIdInput.setAttribute('disabled', 'disabled');
          }
        }
      }

      updateButton(variant) {
        if (!this.submitButton) return;

        if (variant.available) {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText?.classList.remove('hidden');
          this.soldOutMessage?.classList.add('hidden');
        } else {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButtonText?.classList.add('hidden');
          this.soldOutMessage?.classList.remove('hidden');
        }
      }

      updatePrice(variant) {
        if (!this.cardWrapper) return;

        const priceContainer = this.cardWrapper.querySelector('.price');
        if (!priceContainer) return;

        const isSale = variant.compare_at_price && variant.compare_at_price > variant.price;

        // Update regular price
        const regularPrice = priceContainer.querySelector('.price__regular .price-item--regular');
        if (regularPrice) {
          regularPrice.textContent = variant.price_formatted;
        }

        // Update sale price
        const saleComparePrice = priceContainer.querySelector('.price__sale .price-item--regular');
        const salePrice = priceContainer.querySelector('.price__sale .price-item--sale');

        if (saleComparePrice && variant.compare_at_price_formatted) {
          saleComparePrice.textContent = variant.compare_at_price_formatted;
        }
        if (salePrice) {
          salePrice.textContent = variant.price_formatted;
        }

        // Toggle sale class
        priceContainer.classList.toggle('price--on-sale', isSale);
        priceContainer.classList.toggle('price--sold-out', !variant.available);
      }

      updateImages(variant) {
        if (!this.cardWrapper) return;

        const mediaLink = this.cardWrapper.querySelector('.card__media .media');
        const cardMedia = this.cardWrapper.querySelector('.card__media');
        if (!mediaLink || !cardMedia) return;

        const mainImg = mediaLink.querySelector('img');

        // Build gallery: featured image first, then gallery images
        var galleryUrls = [];
        if (variant.featured_image) {
          galleryUrls.push(variant.featured_image);
        }
        // Eigene Variantengalerie, sonst die geteilte Produktgalerie.
        // Das Titelbild steht schon vorne, Duplikate filtert indexOf heraus.
        var extraUrls = variant.gallery && variant.gallery.length > 0 ? variant.gallery : null;
        if (!extraUrls && variant.gallery_from_product) {
          extraUrls = this.productGallery;
        }
        if (extraUrls && extraUrls.length > 0) {
          extraUrls.forEach(function (url) {
            if (url && galleryUrls.indexOf(url) === -1) {
              galleryUrls.push(url);
            }
          });
        }

        // Update main image to featured
        if (variant.featured_image && mainImg) {
          this.swapImage(mainImg, variant.featured_image, variant.featured_image_width, variant.featured_image_height, variant.featured_image_alt);
        }

        // Remove hover effect — carousel replaces it
        mediaLink.classList.remove('media--hover-effect');

        // Remove existing hover image (second img)
        var existingImgs = mediaLink.querySelectorAll('img');
        for (var i = existingImgs.length - 1; i > 0; i--) {
          existingImgs[i].remove();
        }

        // Build or update carousel if more than 1 image
        // Controls go on cardMedia (div), not mediaLink (a tag)
        if (galleryUrls.length > 1) {
          this.buildCarousel(cardMedia, mainImg, galleryUrls);
        } else {
          this.removeCarousel(cardMedia);
        }
      }

      buildCarousel(controlsContainer, mainImg, galleryUrls) {
        // Remove old carousel controls if present
        this.removeCarousel(controlsContainer);
        controlsContainer.classList.add('card__media--carousel');
        if (this.cardWrapper) this.cardWrapper.classList.add('card-wrapper--carousel');

        var self = this;
        this._carouselIndex = 0;
        this._carouselUrls = galleryUrls;
        this._carouselMainImg = mainImg;

        // Create prev arrow
        var prevArrow = document.createElement('button');
        prevArrow.type = 'button';
        prevArrow.className = 'subcollection-inline__img-arrow subcollection-inline__img-arrow--prev';
        prevArrow.innerHTML = '&#8249;';
        prevArrow.setAttribute('aria-label', 'Previous image');

        // Create next arrow
        var nextArrow = document.createElement('button');
        nextArrow.type = 'button';
        nextArrow.className = 'subcollection-inline__img-arrow subcollection-inline__img-arrow--next';
        nextArrow.innerHTML = '&#8250;';
        nextArrow.setAttribute('aria-label', 'Next image');

        // Create dots container
        var dotsWrap = document.createElement('div');
        dotsWrap.className = 'subcollection-inline__img-dots';
        for (var i = 0; i < galleryUrls.length; i++) {
          var dot = document.createElement('span');
          dot.className = 'subcollection-inline__img-dot';
          if (i === 0) dot.classList.add('subcollection-inline__img-dot--active');
          dot.dataset.index = i;
          dotsWrap.appendChild(dot);
        }

        // Append to card__media div (not the <a> tag)
        controlsContainer.appendChild(prevArrow);
        controlsContainer.appendChild(nextArrow);
        controlsContainer.appendChild(dotsWrap);

        // Update arrow visibility
        prevArrow.classList.add('hidden');

        // Event listeners
        prevArrow.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          self.navigateCarousel(-1);
        });

        nextArrow.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          self.navigateCarousel(1);
        });

        dotsWrap.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var dot = e.target.closest('.subcollection-inline__img-dot');
          if (dot) {
            self.goToCarouselSlide(parseInt(dot.dataset.index));
          }
        });

        this._carouselPrev = prevArrow;
        this._carouselNext = nextArrow;
        this._carouselDots = dotsWrap;

        // Preload all gallery images for instant display
        galleryUrls.forEach(function (url) {
          var preload = new Image();
          preload.src = url;
        });

        // Set main image to eager loading
        if (mainImg) mainImg.setAttribute('loading', 'eager');

        // Make image clickable since the card overlay has pointer-events: none
        var mediaDiv = controlsContainer.querySelector('.media');
        if (mediaDiv && !mediaDiv._carouselClickHandler) {
          var productLink = self.cardWrapper ? self.cardWrapper.querySelector('.card__heading a') : null;
          var productUrl = productLink ? productLink.getAttribute('href') : null;
          if (productUrl) {
            mediaDiv.style.cursor = 'pointer';
            mediaDiv._carouselClickHandler = function () {
              window.location.href = productUrl;
            };
            mediaDiv.addEventListener('click', mediaDiv._carouselClickHandler);
          }
        }
      }

      removeCarousel(controlsContainer) {
        controlsContainer.querySelectorAll('.subcollection-inline__img-arrow').forEach(function (el) { el.remove(); });
        controlsContainer.querySelectorAll('.subcollection-inline__img-dots').forEach(function (el) { el.remove(); });
        controlsContainer.classList.remove('card__media--carousel');
        if (this.cardWrapper) this.cardWrapper.classList.remove('card-wrapper--carousel');

        // Remove image click handler
        var mediaDiv = controlsContainer.querySelector('.media');
        if (mediaDiv && mediaDiv._carouselClickHandler) {
          mediaDiv.removeEventListener('click', mediaDiv._carouselClickHandler);
          mediaDiv._carouselClickHandler = null;
          mediaDiv.style.cursor = '';
        }

        this._carouselIndex = 0;
        this._carouselUrls = null;
      }

      navigateCarousel(direction) {
        if (!this._carouselUrls) return;
        var newIndex = this._carouselIndex + direction;
        if (newIndex < 0 || newIndex >= this._carouselUrls.length) return;
        this.goToCarouselSlide(newIndex);
      }

      goToCarouselSlide(index) {
        if (!this._carouselUrls || index < 0 || index >= this._carouselUrls.length) return;
        this._carouselIndex = index;

        var url = this._carouselUrls[index];
        if (this._carouselMainImg) {
          this.swapImage(this._carouselMainImg, url, 0, 0, '');
        }

        // Update dots
        if (this._carouselDots) {
          this._carouselDots.querySelectorAll('.subcollection-inline__img-dot').forEach(function (dot, i) {
            dot.classList.toggle('subcollection-inline__img-dot--active', i === index);
          });
        }

        // Update arrows
        if (this._carouselPrev) {
          this._carouselPrev.classList.toggle('hidden', index === 0);
        }
        if (this._carouselNext) {
          this._carouselNext.classList.toggle('hidden', index === this._carouselUrls.length - 1);
        }
      }

      swapImage(img, url, width, height, alt) {
        const widths = [246, 493, 600, 713, 823, 990, 1100, 1206, 1346, 1426, 1646, 1946];
        const applicableWidths = width > 0 ? widths.filter((w) => w <= width) : widths;

        const srcset = applicableWidths
          .map((w) => `${url.replace(/width=\d+/, `width=${w}`)} ${w}w`)
          .join(', ');

        img.setAttribute('srcset', srcset);
        img.setAttribute('src', url);
        img.setAttribute('loading', 'eager');
        if (width > 0) img.setAttribute('width', width);
        if (height > 0) img.setAttribute('height', height);
        if (alt !== undefined) img.setAttribute('alt', alt || '');
      }
    }
  );
}
